import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { ImageRecord, AudioRecord } from '../../../services/api/glorify'
import { useDashboardSession } from '../../../hooks/useDashboardSession'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function userInitials(displayName: string | undefined, email: string): string {
  if (displayName) {
    return displayName
      .split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return (email[0] ?? '?').toUpperCase()
}

function roleBadgeClass(role: string): string {
  if (role === 'admin') return 'gd-role-badge gd-role-badge--admin'
  if (role === 'partner') return 'gd-role-badge gd-role-badge--partner'
  return 'gd-role-badge gd-role-badge--user'
}

function formatDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return isoString
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OverviewPage(): React.JSX.Element {
  const { user } = useDashboardSession()
  const [images, setImages] = useState<ImageRecord[]>([])
  const [audios, setAudios] = useState<AudioRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const hasAvatar = Boolean(user.avatar_url)

  useEffect(() => {
    let cancelled = false

    async function fetchData(): Promise<void> {
      try {
        const [imgsResp, audsResp] = await Promise.all([
          glorifyApiClient.listImages(),
          glorifyApiClient.listAudios(),
        ])
        if (!cancelled) {
          setImages(imgsResp.images)
          setAudios(audsResp.audios)
        }
      } catch (err) {
        if (!cancelled) {
          setFetchError(
            err instanceof GlorifyApiError
              ? err.message
              : 'Failed to load overview data'
          )
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchData()
    return () => {
      cancelled = true
    }
  }, [])

  const mostRecentImage = images[0]?.image_file_name
  const mostRecentAudio = audios[0]?.audio_file_name

  const greeting = user.display_name
    ? `Welcome back, ${user.display_name.split(' ')[0]}!`
    : `Welcome back!`

  return (
    <>
      {/* Page header */}
      <div className="gd-page-header">
        <h1>{greeting}</h1>
        <p>Here's a summary of your Partners Portal activity.</p>
      </div>

      {fetchError && (
        <div className="gd-banner gd-banner--error" role="alert">
          <span className="material-icons" aria-hidden="true">warning</span>
          <p>{fetchError}</p>
        </div>
      )}

      {/* Top row: profile + stats */}
      <div className="gd-stats-row">
        {/* Profile card */}
        <div className="gd-card gd-card--elevated" style={{ gridColumn: 'span 1' }}>
          <div className="gd-profile-card">
            <div
              className="gd-avatar gd-profile-card__avatar"
              role="img"
              aria-label={`Avatar for ${user.display_name ?? user.user_email}`}
            >
              {hasAvatar ? (
                <img src={user.avatar_url} alt="" />
              ) : (
                userInitials(user.display_name, user.user_email)
              )}
            </div>
            <div className="gd-profile-card__meta">
              {user.display_name && (
                <span className="gd-profile-card__name">{user.display_name}</span>
              )}
              <span className="gd-profile-card__email">{user.user_email}</span>
              <span className={roleBadgeClass(user.user_role)} style={{ marginTop: 4 }}>
                {user.user_role}
              </span>
              <span className="gd-profile-card__since">
                Member since {formatDate(user.user_created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Image stat card */}
        <div className="gd-card gd-card--elevated">
          <div className="gd-stat-card">
            <div className="gd-stat-card__icon gd-stat-card__icon--primary">
              <span className="material-icons" aria-hidden="true">image</span>
            </div>
            <div>
              <div className="gd-stat-card__number">
                {isLoading ? '—' : images.length}
              </div>
              <div className="gd-stat-card__label">Images uploaded</div>
              {mostRecentImage && (
                <div className="gd-stat-card__recent" title={mostRecentImage}>
                  Latest: {mostRecentImage}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Audio stat card */}
        <div className="gd-card gd-card--elevated">
          <div className="gd-stat-card">
            <div className="gd-stat-card__icon gd-stat-card__icon--tertiary">
              <span className="material-icons" aria-hidden="true">audio_file</span>
            </div>
            <div>
              <div className="gd-stat-card__number">
                {isLoading ? '—' : audios.length}
              </div>
              <div className="gd-stat-card__label">Audio files uploaded</div>
              {mostRecentAudio && (
                <div className="gd-stat-card__recent" title={mostRecentAudio}>
                  Latest: {mostRecentAudio}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Markets link */}
        <Link to="/dashboard/markets" className="gd-card gd-card--elevated" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="gd-stat-card">
            <div className="gd-stat-card__icon gd-stat-card__icon--primary">
              <span className="material-icons" aria-hidden="true">show_chart</span>
            </div>
            <div>
              <div className="gd-stat-card__number" style={{ fontSize: 18 }}>Markets</div>
              <div className="gd-stat-card__label">
                Index levels, movers &amp; sector performance from Financial Modeling Prep.
              </div>
            </div>
          </div>
        </Link>

        {/* Options calculator link */}
        <Link to="/dashboard/options-calculator" className="gd-card gd-card--elevated" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="gd-stat-card">
            <div className="gd-stat-card__icon gd-stat-card__icon--tertiary">
              <span className="material-icons" aria-hidden="true">calculate</span>
            </div>
            <div>
              <div className="gd-stat-card__number" style={{ fontSize: 18 }}>Options Calculator</div>
              <div className="gd-stat-card__label">
                Model payoff, max profit/loss &amp; breakevens for vertical spreads.
              </div>
            </div>
          </div>
        </Link>
      </div>
    </>
  )
}
